import { Badge, Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { getEventCalculatorOptions } from "../../../services/eventCalculator";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

// Shows, per categoria da calculadora de eventos, every Produto that
// actually resolves into it today — a Produto's own event_drink_category
// override if set, otherwise its Tipo de Produto's — so an admin can catch a
// category with zero products mapped (invisible otherwise until it fails to
// show up live in the calculator) at a glance, without testing the wizard.
export function CalculatorCurationCard({ allProductTypes, allProducts }) {
  const [drinkCategories, setDrinkCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDrinkCategories() {
      try {
        const response = await getEventCalculatorOptions();
        setDrinkCategories(response.data.catalog_drink_categories || []);
      } catch (error) {
        console.error("Error fetching catalog drink categories:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchDrinkCategories();
  }, []);

  const productTypeById = useMemo(
    () => Object.fromEntries((allProductTypes || []).map(pt => [pt.id, pt])),
    [allProductTypes]
  );

  function resolvedCategoryOf(product) {
    return product.event_drink_category || productTypeById[product.product_type_id]?.event_drink_category || null;
  }

  const categoryBreakdown = useMemo(() => {
    return drinkCategories.map(dc => {
      const typesMapped = (allProductTypes || []).filter(pt => pt.event_drink_category === dc.key);
      const productsResolved = (allProducts || []).filter(p => resolvedCategoryOf(p) === dc.key);
      const productsOverridden = productsResolved.filter(p => !!p.event_drink_category);
      return { ...dc, typesMapped, productsResolved, productsOverridden };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drinkCategories, allProductTypes, allProducts, productTypeById]);

  const emptyCount = categoryBreakdown.filter(c => c.productsResolved.length === 0).length;

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px" wrap="wrap" gap="10px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Calculadora de eventos
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Curadoria de categorias
          </Text>
        </Box>
        {!loading && emptyCount > 0 && (
          <Flex align="center" gap="6px" bg="rgba(252,143,4,.08)" border="1px solid rgba(252,143,4,.3)" borderRadius="8px" px="10px" py="6px">
            <FiAlertTriangle size={13} color={ORANGE} />
            <Text fontSize="12px" fontWeight="700" color="#b76800">
              {emptyCount} categoria{emptyCount !== 1 ? "s" : ""} sem produto mapeado
            </Text>
          </Flex>
        )}
      </Flex>

      <Text fontSize="12px" color="gray.500" mb="18px">
        Mostra quais produtos cada categoria da calculadora vai efetivamente sugerir — via o campo "Categoria da calculadora"
        do Tipo de Produto, ou um override direto no produto. Categorias sem nenhum produto resolvido aparecem destacadas.
      </Text>

      {loading ? (
        <Text fontSize="13px" color="gray.400">Carregando…</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="14px">
          {categoryBreakdown.map(cat => {
            const isEmpty = cat.productsResolved.length === 0;
            return (
              <Box
                key={cat.key}
                bg="white" borderRadius="14px" p="16px"
                border={`1px solid ${isEmpty ? "rgba(252,143,4,.35)" : "rgba(0,74,51,.08)"}`}
                boxShadow="0 2px 10px rgba(0,74,51,.05)"
              >
                <Flex align="center" justify="space-between" mb="8px">
                  <Text fontSize="13px" fontWeight="800" color={GREEN}>{cat.label}</Text>
                  <Badge
                    fontSize="9px" borderRadius="4px"
                    colorScheme={isEmpty ? "orange" : "green"}
                  >
                    {cat.productsResolved.length} produto{cat.productsResolved.length !== 1 ? "s" : ""}
                  </Badge>
                </Flex>

                {isEmpty ? (
                  <Text fontSize="12px" color="#b76800" fontWeight="600">
                    Nenhum produto cai nessa categoria hoje — vai aparecer vazia na calculadora.
                  </Text>
                ) : (
                  <Box display="flex" flexDirection="column" gap="4px" maxH="140px" overflowY="auto">
                    {cat.productsResolved.map(p => (
                      <Flex key={p.id} justify="space-between" align="center" gap="6px">
                        <Text fontSize="11px" color="gray.600" noOfLines={1}>{p.name}</Text>
                        {p.event_drink_category && (
                          <Text fontSize="9px" color="gray.400" flexShrink={0}>no produto</Text>
                        )}
                      </Flex>
                    ))}
                  </Box>
                )}

                {cat.typesMapped.length > 0 && (
                  <Text fontSize="10px" color="gray.400" mt="8px">
                    Herdado de {cat.typesMapped.length} Tipo{cat.typesMapped.length !== 1 ? "s" : ""} de Produto: {cat.typesMapped.map(t => t.name).join(", ")}
                  </Text>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      )}
    </Box>
  );
}
